'use client';
import { useTranslations } from 'next-intl';
import { type ReactNode, useEffect, useState } from 'react';
import { Loading } from '@/app/components/icons/Loading';
import { computeClawPassTotalCents } from '@/app/components/shared/ClawPassContent/utils';
import { Button } from '@/app/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/app/components/ui/dialog';
import { useFetch } from '@/app/hooks';
import type { ClawPassProduct, ProductType } from '@/app/types';
import { formatPrice, normalizeSelectedCouponId, reportEvent } from '@/app/utils';
import { couponsListMyCoupons, ordersGetProductWithOption } from '../apis';
import type { CreditV1Coupon, CreditV1PeriodProduct } from '../types';
import { CouponSelect } from './CouponSelect';

interface StripeConfirmDialogProps {
  product: ProductType;
  open: boolean;
  onClose: () => void;
  onConfirm: (couponId: string | null) => void;
  /** 购买时长 / 份数（claw 权益包多月购买）。缺省按 1 计，保持原有调用方行为不变。 */
  quantity?: number;
  /** 标题下、优惠券上方的附加信息区（如 claw 续购的生效说明）。 */
  extraInfo?: ReactNode;
}

export const StripeConfirmDialog = ({
  product,
  open,
  onClose,
  onConfirm,
  quantity = 1,
  extraInfo,
}: StripeConfirmDialogProps) => {
  const t = useTranslations('payments.stripeConfirm');
  const p = product as CreditV1PeriodProduct;
  const effectiveQuantity = quantity > 1 ? quantity : 1;

  const [selectedCouponId, setSelectedCouponId] = useState<string | null>(() =>
    normalizeSelectedCouponId(p.selected_coupon_id),
  );

  // 弹窗打开时曝光埋点
  useEffect(() => {
    if (open) {
      const fromPage =
        product.product_pay_status === 'upgrade'
          ? 'upgrade'
          : product.product_pay_status === 'downgrade'
            ? 'downgrade'
            : product.product_type === 'credits_purchase'
              ? 'purchase_credits'
              : 'subscribe';
      reportEvent('check_out_exposure', { from_page: fromPage });
    }
  }, [open]);

  // 拉取可用券列表
  const { data: couponsData, loading: couponsLoading } = useFetch(
    () => couponsListMyCoupons({ query: { product_id: p.id as number, status: 'unused', 'pager.pagesz': 100 } }),
    {
      ready: open && p.id !== undefined,
      refreshDeps: [p.id],
      onSuccess: ({ data }) => {
        const coupons = data?.coupons ?? [];
        setSelectedCouponId((prev) => (prev && coupons.find((c) => c.id === prev) ? prev : null));
      },
    },
  );
  const availableCoupons: CreditV1Coupon[] = couponsData?.data?.coupons ?? p.available_coupons ?? [];

  // 切换券时查价：等券列表就绪后才触发，避免双请求
  const {
    data: priceData,
    loading: loadingPrice,
    error: priceError,
  } = useFetch(
    () =>
      ordersGetProductWithOption({
        path: { product_id: p.id as number },
        query: {
          ...(selectedCouponId ? { coupon_id: selectedCouponId } : {}),
          ...(effectiveQuantity > 1 ? { quantity: effectiveQuantity } : {}),
        },
      }),
    {
      ready: open && !couponsLoading && p.id !== undefined,
      refreshDeps: [selectedCouponId, p.id, effectiveQuantity, couponsLoading],
      // 查价失败已通过 priceError 在 UI 上降级（"--" + 禁用按钮），
      // 抑制 useFetch 默认的全局 error toast 避免重复提示
      onError: () => {},
    },
  );

  const displayPrice = priceData?.data?.price ?? null;
  // baseCents 的 fallback：多份场景按 N 份无券实付（首份 price + 其余 original_price），
  // 单份场景直接用 product.price，避免 loading 期间落到单份首购价
  const baseCents =
    displayPrice ??
    (effectiveQuantity > 1 ? computeClawPassTotalCents(product as ClawPassProduct, effectiveQuantity) : (p.price ?? 0));
  const currentPrice = baseCents;
  // 划线原价：多份 = original_price × N（体现首购折扣 + 优惠券总优惠值）；单份直接读 original_price
  const originalDisplayPrice =
    effectiveQuantity > 1 ? (p.original_price ?? p.price ?? 0) * effectiveQuantity : (p.original_price ?? currentPrice);
  const hasCouponDiscount = Boolean(selectedCouponId && displayPrice !== null);
  // 「优惠券减免」行仅在选券时展示（语义对齐）；首购折扣等其它优惠由原价划线行体现。
  const discountAmount = hasCouponDiscount ? Math.max(0, originalDisplayPrice - currentPrice) : 0;
  const hasCoupons = availableCoupons.length > 0;

  return (
    <Dialog
      open={open}
      onOpenChange={(isOpen) => {
        if (!isOpen) onClose();
      }}
    >
      <DialogContent className="flex w-[400px] max-w-[400px] flex-col gap-6 p-6 sm:p-4">
        <DialogHeader className="text-left text-system-white">
          <DialogTitle className="font-semibold text-xl">{t('title')}</DialogTitle>
        </DialogHeader>

        {extraInfo}

        {hasCoupons && (
          <CouponSelect
            availableCoupons={availableCoupons}
            selectedCouponId={selectedCouponId}
            onChange={(id) => {
              reportEvent('check_out_popup_click', { element_name: 'chose_coupon' });
              setSelectedCouponId(id);
            }}
          />
        )}

        {/* 价格明细 */}
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between text-sm text-system-white64">
            <span>{t('originalPrice')}</span>
            <span>{formatPrice(originalDisplayPrice)}</span>
          </div>
          {discountAmount > 0 && (
            <div className="flex items-center justify-between text-sm">
              <span className="text-system-white64">{t('couponDiscount')}</span>
              <span className="text-[#5765f2]">-{formatPrice(discountAmount)}</span>
            </div>
          )}
          <div className="flex items-center justify-between font-semibold text-base text-white">
            <span>{t('total')}</span>
            <span>
              {loadingPrice ? (
                <Loading className="animate-spin text-base" />
              ) : priceError ? (
                <span className="text-sm text-system-white64">--</span>
              ) : (
                formatPrice(currentPrice)
              )}
            </span>
          </div>
        </div>

        <Button
          variant="primary"
          size="lg"
          className="h-11 w-full"
          disabled={loadingPrice || !!priceError}
          onClick={() => {
            reportEvent('check_out_popup_click', { element_name: 'check_out' });
            onConfirm(selectedCouponId);
          }}
        >
          {t('confirmButton')}
        </Button>
      </DialogContent>
    </Dialog>
  );
};
